import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ATTRACT_TIMING, buildAttractCampaign } from '../data/attract';
import type { CharacterProfile } from '../types';
import { loadCampaignIdentity, nextCampaignIdentity } from '../lib/campaignPlanner';

interface ControllerState {
  entryIndex: number;
  sceneElapsedMs: number;
}

export function useAttractController(characters: CharacterProfile[], enabled: boolean, playbackRate = 1) {
  const [campaignIdentity, setCampaignIdentity] = useState(loadCampaignIdentity);
  const entries = useMemo(() => buildAttractCampaign(characters, campaignIdentity), [campaignIdentity, characters]);
  const [state, setState] = useState<ControllerState>({ entryIndex: 0, sceneElapsedMs: 0 });
  const [runId, setRunId] = useState(0);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const entryIndexRef = useRef(0);
  const entryElapsedRef = useRef(0);
  const lastTickRef = useRef(performance.now());
  const playbackRateRef = useRef(playbackRate);
  const rafRef = useRef<number | null>(null);

  useEffect(() => { playbackRateRef.current = playbackRate; }, [playbackRate]);

  const resetTimeline = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    entryIndexRef.current = 0;
    entryElapsedRef.current = 0;
    lastTickRef.current = performance.now();
    setManuallyPaused(false);
    setState({ entryIndex: 0, sceneElapsedMs: 0 });
    setRunId((current) => current + 1);
  }, []);

  const restart = useCallback(() => {
    setCampaignIdentity((current) => nextCampaignIdentity(current));
    resetTimeline();
  }, [resetTimeline]);

  const jumpToEntry = useCallback((requestedIndex: number, requestedElapsedMs = 0) => {
    const targetIndex = Math.max(0, Math.min(entries.length - 1, Math.floor(requestedIndex)));
    const targetDuration = entries[targetIndex].durationMs;
    const targetElapsedMs = Math.max(0, Math.min(requestedElapsedMs, Number.isFinite(targetDuration) ? Math.max(0, targetDuration - 1) : requestedElapsedMs));
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    entryIndexRef.current = targetIndex;
    entryElapsedRef.current = targetElapsedMs;
    lastTickRef.current = performance.now();
    setState({ entryIndex: targetIndex, sceneElapsedMs: targetElapsedMs });
    setRunId((current) => current + 1);
  }, [entries]);

  const pause = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setManuallyPaused(true);
  }, []);

  const resume = useCallback(() => {
    lastTickRef.current = performance.now();
    setManuallyPaused(false);
  }, []);

  useEffect(() => {
    if (!enabled || manuallyPaused) return;

    const tick = (now: number) => {
      let index = entryIndexRef.current;
      const delta = Math.max(0, now - lastTickRef.current) * playbackRateRef.current;
      lastTickRef.current = now;
      let elapsed = entryElapsedRef.current + delta;
      while (index < entries.length - 1 && elapsed >= entries[index].durationMs) {
        elapsed -= entries[index].durationMs;
        index += 1;
        entryIndexRef.current = index;
      }
      entryElapsedRef.current = elapsed;
      setState((current) => current.entryIndex === index && Math.abs(current.sceneElapsedMs - elapsed) < 32
        ? current
        : { entryIndex: index, sceneElapsedMs: elapsed });
      if (entries[index].type !== 'finale') rafRef.current = requestAnimationFrame(tick);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      } else {
        lastTickRef.current = performance.now();
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    lastTickRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, entries, manuallyPaused, runId]);

  const entry = entries[state.entryIndex];
  const imageIndex = entry.type === 'character'
    ? Math.min(3, Math.floor(state.sceneElapsedMs / ATTRACT_TIMING.characterImageDurationMs))
    : 0;
  const frameProgress = entry.type === 'character'
    ? (state.sceneElapsedMs % ATTRACT_TIMING.characterImageDurationMs) / ATTRACT_TIMING.characterImageDurationMs
    : Math.min(1, state.sceneElapsedMs / (Number.isFinite(entry.durationMs) ? entry.durationMs : 1));

  return { entries, entry, entryIndex: state.entryIndex, sceneElapsedMs: state.sceneElapsedMs, imageIndex, frameProgress, begin: resetTimeline, restart, jumpToEntry, pause, resume, isPaused: manuallyPaused, playbackRate };
}
